import { useEffect, useState } from "react";
import { Loader2, Save, AlertCircle } from "lucide-react";
import API from "../../api/api";

const types = [
  { id: "privacy", label: "Privacy Policy" },
  { id: "terms", label: "Terms & Conditions" },
];

export default function Legal() {
  const [content, setContent] = useState({ privacy: "", terms: "" });
  const [status, setStatus] = useState({ privacy: "Active", terms: "Active" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchLegal = async () => {
    try {
      setLoading(true);
      setError("");
      const results = await Promise.allSettled(types.map((t) => API.get(`/legal/${t.id}`)));
      const nextContent = { ...content };
      const nextStatus = { ...status };
      results.forEach((res, idx) => {
        if (res.status === "fulfilled" && res.value?.data?.legal) {
          const { legal } = res.value.data;
          nextContent[types[idx].id] = legal.content || "";
          nextStatus[types[idx].id] = legal.status || "Active";
        }
      });
      setContent(nextContent);
      setStatus(nextStatus);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load legal content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLegal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveType = async (type) => {
    try {
      setSaving(true);
      setError("");
      await API.put(`/admin/legal/${type}`, { content: content[type], status: status[type] });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Legal Pages</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Edit Privacy Policy and Terms</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {types.map((t) => (
          <div key={t.id} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">{t.label}</p>
                <p className="text-xs text-gray-500">Type: {t.id}</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={status[t.id]}
                  onChange={(e) => setStatus((prev) => ({ ...prev, [t.id]: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-sm"
                >
                  <option value="Active">Active</option>
                  <option value="Draft">Draft</option>
                </select>
                <button
                  onClick={() => saveType(t.id)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </div>
            <textarea
              rows={10}
              value={content[t.id]}
              onChange={(e) => setContent((prev) => ({ ...prev, [t.id]: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-sm"
              placeholder={`Write ${t.label} content...`}
            />
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading existing content...
        </div>
      )}
    </div>
  );
}
